import { Type } from "class-transformer";
import {
    Entity,
    ManyToOne,
    PrimaryColumn,
    Column,
    JoinColumn,
    OneToOne,
    CreateDateColumn,
    PrimaryGeneratedColumn,
    Index,
    UpdateDateColumn,
    ManyToMany,
} from "typeorm";
import { Account, BonusesBalance, Company, Employee, TimeBalance, TimeEntry, VacationBalance } from "./model";
import { toDateString } from "./utils";
import { getVersion } from "./version";
import { ClientInfo } from "./transport";
import { DateString } from "@pentacode/openapi";
import { Issue } from "./issues";
import { TimeResult } from "./time";

@Entity()
export class APIClient {
    constructor(vals: Partial<APIClient> = {}) {
        Object.assign(this, vals);
    }
    @PrimaryColumn()
    id: string;

    @Column()
    secret: string;

    @Column()
    name: string;

    @Column()
    email: string;

    @Column()
    website: string;
}

@Entity()
export class APIAuthAccess {
    constructor(vals: Partial<APIAuthAccess> = {}) {
        Object.assign(this, vals);
    }
    @PrimaryGeneratedColumn()
    id: number;

    @Column()
    companyId: number;

    @ManyToOne(() => Company, { onDelete: "CASCADE" })
    company: Company;

    @Column()
    clientId: string;

    @ManyToOne(() => APIClient, { onDelete: "CASCADE" })
    client: APIClient;

    @Column("jsonb")
    scopes: string[] = [];
}

@Entity()
export class APIAuthToken {
    constructor(init: Partial<APIAuthToken> = {}) {
        Object.assign(this, init);
    }

    @PrimaryColumn()
    id: string;

    @CreateDateColumn()
    @Type(() => Date)
    created: Date;

    @Column()
    token: string;

    @Column("jsonb")
    scopes: string[] = [];

    @Column({ nullable: true })
    clientId?: string;

    @ManyToOne(() => APIClient, { onDelete: "CASCADE", nullable: true })
    client?: APIClient;

    @Column()
    companyId: number;

    @ManyToOne(() => Company, { onDelete: "CASCADE" })
    company: Company;

    @Column("timestamptz", { nullable: true })
    @Type(() => Date)
    expires: Date | null;
}

/**
 * @deprecated
 */

@Entity()
export class MonthlyStatement {
    @ManyToOne(() => Employee, { onDelete: "CASCADE", eager: false })
    employee: Employee;

    @PrimaryColumn()
    employeeId: number;

    @PrimaryColumn()
    year: number;

    @PrimaryColumn()
    month: number;

    @Column()
    @Type(() => Date)
    updated: Date = new Date();

    @Column()
    version: string = getVersion();

    @Column("jsonb", {
        nullable: true,
    })
    reset: Partial<TimeEntry> | null = null;

    @Column("jsonb")
    time: unknown;

    @Column("jsonb")
    vacation: unknown;

    @Column("jsonb")
    bonus: unknown;

    @Column("jsonb")
    cost: unknown;

    @Column("jsonb", { select: true })
    items: unknown;

    @Column("jsonb")
    issues: unknown[] = [];

    @Column("jsonb", { nullable: true })
    previousAverages?: unknown;

    get from() {
        return toDateString(new Date(this.year, this.month, 1));
    }

    get to() {
        return toDateString(new Date(this.year, this.month + 1, 1));
    }

    constructor(vals: Partial<MonthlyStatement> = {}) {
        Object.assign(this, vals);
    }
}

@Entity()
export class Session {
    constructor(vals: Partial<Session> = {}) {
        Object.assign(this, vals);
    }

    @PrimaryGeneratedColumn()
    id: number;

    @ManyToOne(() => Account, (a) => a.sessions, { onDelete: "CASCADE" })
    @Type(() => Account)
    account: Account;

    @Column()
    accountId: number;

    @ManyToOne(() => Company, { nullable: true, onDelete: "CASCADE" })
    @Type(() => Company)
    company?: Company | null;

    @Column({ nullable: true })
    companyId: number | null;

    @Column()
    token: string;

    @CreateDateColumn()
    created: Date;

    @Column({ nullable: true })
    scope: string;

    @Column({ type: Date, nullable: true })
    expires: Date | null;

    @Column({ type: Date, nullable: true })
    lastUsed: Date | null;

    @Column({ default: false })
    revoked: boolean = false;

    @Column({ type: "jsonb", nullable: true })
    clientInfo: ClientInfo | null = null;
}

@Entity()
export class TermsState {
    // new companies need to accept immediately to use Pentacode
    static defaultDeadline() {
        return new Date();
    }

    constructor(init: Partial<TermsState> = {}) {
        Object.assign(this, init);
    }

    @PrimaryColumn({ nullable: false })
    companyId: number;

    @OneToOne(() => Company, { nullable: true, onDelete: "CASCADE" })
    @JoinColumn({ name: "companyId", referencedColumnName: "id" })
    company?: Company;

    @Column("timestamptz", { nullable: true, default: null })
    processingAgreementAccepted: Date | null = null;

    @Column("timestamptz", { nullable: true, default: null })
    termsAccepted: Date | null = null;

    @Column("timestamptz", {
        nullable: false,
    })
    deadline: Date;
}

@Entity()
@Index("IDX_employee_date_issues", ["employeeId", "date", "issues"], { unique: true })
export class DailyResults {
    constructor(init: Partial<DailyResults> = {}) {
        Object.assign(this, init);
    }

    @ManyToOne(() => Employee, { onDelete: "CASCADE", eager: false })
    employee: Employee;

    @PrimaryColumn()
    employeeId: number;

    @PrimaryColumn("date")
    date: DateString;

    @Column("jsonb")
    timeBalance: Omit<TimeBalance, "employeeId" | "from" | "to">;

    @Column("jsonb", { nullable: true })
    timeBalancePlanned: Omit<TimeBalance, "employeeId" | "from" | "to"> | null = null;

    @Column("jsonb")
    vacationBalance: Omit<VacationBalance, "employeeId" | "from" | "to">;

    @Column("jsonb")
    bonusesBalance: Omit<BonusesBalance, "employeeId" | "from" | "to">;

    @Column("jsonb")
    results: {
        work: TimeResult;
        workPlanned?: TimeResult;
        absences: TimeResult;
        adjustments: TimeResult;
        total: TimeResult;
    };

    @Column("jsonb")
    averages: {
        "13weeks": TimeResult;
    };

    @UpdateDateColumn()
    @Type(() => Date)
    updated: Date;

    @Column("jsonb", { nullable: true })
    issues: Issue[] | null = null;
}

@Entity()
export class EmployeeTag {
    constructor(init: Partial<EmployeeTag> = {}) {
        Object.assign(this, init);
    }

    @PrimaryGeneratedColumn()
    id: number;

    @Column()
    name: string;

    @Column()
    color: string;

    @ManyToOne(() => Company, { onDelete: "CASCADE", eager: false })
    company?: Company;

    @Column()
    @Index()
    companyId: number;

    @ManyToMany(() => Employee, (employee) => employee.tags, { eager: false })
    employees: Employee[];

    @Column({ default: 0 })
    order: number = 0;
}
